// Kit partenariats — lettre type + e-mails de premier contact, en 4 langues.
// Champs entre [ ] à personnaliser. Affiché dans le Dashboard admin (onglet Partenariats).

export const ORGS = [
  { id: "teded", name: "TED-Ed" },
  { id: "universcience", name: "Universcience" },
  { id: "arte", name: "Arte" },
  { id: "wikimedia", name: "Wikimedia" },
];

export const LETTERS = {
  fr: {
    langLabel: "Français",
    fullTitle: "Lettre type de partenariat",
    full: `Objet : Proposition de partenariat & collaboration — Cocoon Mind × [Organisation]

Bonjour [Nom / Équipe partenariats],

Je m'appelle [Votre nom] et je suis à l'origine de Cocoon Mind (https://cocoonmind.com), un compagnon d'IA personnel conçu pour cultiver la curiosité et un rapport apaisé et joyeux au savoir — loin de la pression des notes. Nous accompagnons apprenants et familles en quatre langues (français, anglais, espagnol, néerlandais), avec une attention forte à l'éthique, à la vie privée (RGPD) et à la protection des enfants.

Je vous écris parce que [Organisation] résonne profondément avec notre mission. [Phrase spécifique — ex. : « Votre travail pour rendre les sciences accessibles à tous incarne exactement l'esprit que nous voulons cultiver. »] Ce serait un honneur d'explorer une collaboration.

Ce que nous pourrions offrir à [Organisation] :
• Une visibilité auprès de notre audience grandissante de curieux et de familles en Europe
• Une mise en avant sur notre site et dans l'application, avec un lien vers vos ressources
• Des contenus co-créés, des activités communes ou de la promotion croisée — ouverts à vos idées

Ce que nous vous demanderions :
• Votre accord pour afficher votre logo et votre nom sur notre site, avec un lien vers [Organisation]
• L'accès à vos ressources de marque officielles et à leurs règles d'usage (brand / press kit)

Tout se ferait bien sûr dans le respect de votre charte et serait soumis à votre validation avant mise en ligne.

Seriez-vous ouvert(e) à un court échange dans les prochaines semaines ? Je serais ravi(e) de vous présenter Cocoon Mind et de répondre à vos questions.

Merci pour votre temps et pour le travail inspirant que vous menez.

Bien à vous,
[Votre nom]
Fondateur / Fondatrice — Cocoon Mind
[E-mail] · [Téléphone] · https://cocoonmind.com`,
    shortTitle: "E-mails de premier contact (courts)",
    short: {
      teded: `Objet : TED-Ed × Cocoon Mind — cultiver la curiosité ensemble

Bonjour,
Je suis [Votre nom], fondateur de Cocoon Mind (cocoonmind.com), un compagnon IA qui cultive la curiosité sans la pression des notes. Votre travail avec TED-Ed nous inspire beaucoup. J'aimerais explorer une mise en avant mutuelle (logo + lien) et d'éventuels contenus communs. Seriez-vous ouvert à un court échange ?
Merci, [Votre nom] — [E-mail]`,
      universcience: `Objet : Universcience × Cocoon Mind — le savoir accessible à tous

Bonjour,
Je suis [Votre nom], fondateur de Cocoon Mind (cocoonmind.com), un compagnon IA qui cultive la curiosité au-delà des notes. Votre mission de partage des sciences avec tous les publics résonne profondément avec la nôtre. Pourrions-nous échanger sur une collaboration (mise en avant, contenus, lien) ?
Merci, [Votre nom] — [E-mail]`,
      arte: `Objet : Arte × Cocoon Mind — nourrir la curiosité culturelle

Bonjour,
Je suis [Votre nom], fondateur de Cocoon Mind (cocoonmind.com), un compagnon IA dédié à la curiosité et au plaisir d'apprendre. Votre exigence culturelle et votre regard sur le monde nous inspirent. Seriez-vous ouvert à explorer une mise en avant croisée et d'éventuels contenus communs ?
Merci, [Votre nom] — [E-mail]`,
      wikimedia: `Objet : Valoriser le savoir libre de Wikimédia auprès de notre communauté

Bonjour,
Je suis [Votre nom], fondateur de Cocoon Mind (cocoonmind.com), un compagnon IA qui cultive la curiosité. Nous souhaitons mettre en avant et créditer le savoir libre de Wikimédia auprès de notre communauté, dans le respect de vos licences et de votre charte de marque. Pourrions-nous échanger sur la bonne manière de le faire ?
Merci, [Votre nom] — [E-mail]`,
    },
  },

  en: {
    langLabel: "English",
    fullTitle: "Partnership letter template",
    full: `Subject: Partnership & collaboration proposal — Cocoon Mind × [Organization]

Dear [Name / Partnerships Team],

My name is [Your Name], founder of Cocoon Mind (https://cocoonmind.com), a personal AI companion designed to nurture curiosity and a calm, joyful relationship with knowledge — away from the pressure of grades. We support learners and families in four languages (French, English, Spanish, Dutch), with a strong focus on ethics, privacy (GDPR) and child safety.

I'm reaching out because [Organization] deeply resonates with our mission. [Specific sentence — e.g., "Your work making science accessible to everyone is exactly the spirit we want to cultivate."] We would be honored to explore a collaboration.

What we could offer [Organization]:
• Visibility to our growing, engaged audience of curious minds and families across Europe
• A featured placement on our website and within the app, linking back to your resources
• Co-created content, joint activities, or cross-promotion — open to your ideas

What we would kindly ask:
• Your agreement to display your logo and name on our site, linking to [Organization]
• Access to your official brand assets and usage guidelines (brand / press kit)

Everything would, of course, follow your brand guidelines and be submitted for your approval before going live.

Would you be open to a short call in the coming weeks to discuss how we might work together? I'd be glad to share more about Cocoon Mind and answer any questions.

Thank you for your time and for the inspiring work you do.

Warm regards,
[Your Name]
Founder — Cocoon Mind
[Email] · [Phone] · https://cocoonmind.com`,
    shortTitle: "First-contact emails (short)",
    short: {
      teded: `Subject: TED-Ed × Cocoon Mind — growing curiosity together

Hello,
I'm [Your Name], founder of Cocoon Mind (cocoonmind.com), an AI companion that nurtures curiosity without the pressure of grades. Your work with TED-Ed truly inspires us. I'd love to explore mutual visibility (logo + link) and possible shared content. Would you be open to a short call?
Thank you, [Your Name] — [Email]`,
      universcience: `Subject: Universcience × Cocoon Mind — knowledge for everyone

Hello,
I'm [Your Name], founder of Cocoon Mind (cocoonmind.com), an AI companion nurturing curiosity beyond grades. Your mission to share science with every audience resonates deeply with ours. Could we talk about a collaboration (feature, content, link)?
Thank you, [Your Name] — [Email]`,
      arte: `Subject: Arte × Cocoon Mind — feeding cultural curiosity

Hello,
I'm [Your Name], founder of Cocoon Mind (cocoonmind.com), an AI companion devoted to curiosity and the joy of learning. Your cultural standards and open view of the world inspire us. Would you be open to exploring cross-promotion and possible shared content?
Thank you, [Your Name] — [Email]`,
      wikimedia: `Subject: Celebrating Wikimedia's free knowledge with our community

Hello,
I'm [Your Name], founder of Cocoon Mind (cocoonmind.com), an AI companion that nurtures curiosity. We'd like to highlight and credit Wikimedia's free knowledge to our community, fully respecting your licenses and brand guidelines. Could we discuss the right way to do this?
Thank you, [Your Name] — [Email]`,
    },
  },

  es: {
    langLabel: "Español",
    fullTitle: "Carta tipo de colaboración",
    full: `Asunto: Propuesta de colaboración — Cocoon Mind × [Organización]

Estimado/a [Nombre / Equipo de alianzas],

Me llamo [Tu nombre] y soy el/la fundador/a de Cocoon Mind (https://cocoonmind.com), un compañero de IA personal diseñado para cultivar la curiosidad y una relación serena y alegre con el conocimiento — lejos de la presión de las notas. Acompañamos a estudiantes y familias en cuatro idiomas (francés, inglés, español, neerlandés), con un fuerte compromiso con la ética, la privacidad (RGPD) y la protección de los menores.

Le escribo porque [Organización] conecta profundamente con nuestra misión. [Frase específica — p. ej.: «Su labor para hacer la ciencia accesible a todos encarna justo el espíritu que queremos cultivar.»] Sería un honor explorar una colaboración.

Lo que podríamos ofrecer a [Organización]:
• Visibilidad ante nuestra creciente audiencia de mentes curiosas y familias en Europa
• Un espacio destacado en nuestro sitio y en la app, con enlace a sus recursos
• Contenido co-creado, actividades conjuntas o promoción cruzada — abiertos a sus ideas

Lo que le pediríamos:
• Su acuerdo para mostrar su logotipo y nombre en nuestro sitio, con enlace a [Organización]
• Acceso a sus recursos de marca oficiales y a sus normas de uso (brand / press kit)

Todo se haría, por supuesto, respetando sus directrices de marca y con su validación previa a la publicación.

¿Estaría abierto/a a una breve conversación en las próximas semanas? Me encantaría presentarle Cocoon Mind y responder a sus preguntas.

Gracias por su tiempo y por el trabajo tan inspirador que realizan.

Un cordial saludo,
[Tu nombre]
Fundador/a — Cocoon Mind
[Correo] · [Teléfono] · https://cocoonmind.com`,
    shortTitle: "Correos de primer contacto (breves)",
    short: {
      teded: `Asunto: TED-Ed × Cocoon Mind — cultivar la curiosidad juntos

Hola:
Soy [Tu nombre], fundador/a de Cocoon Mind (cocoonmind.com), un compañero de IA que cultiva la curiosidad sin la presión de las notas. Su trabajo con TED-Ed nos inspira. Me encantaría explorar una visibilidad mutua (logo + enlace) y posibles contenidos comunes. ¿Estaría abierto/a a una breve charla?
Gracias, [Tu nombre] — [Correo]`,
      universcience: `Asunto: Universcience × Cocoon Mind — el saber para todos

Hola:
Soy [Tu nombre], fundador/a de Cocoon Mind (cocoonmind.com), un compañero de IA que cultiva la curiosidad más allá de las notas. Su misión de compartir la ciencia con todos los públicos conecta con la nuestra. ¿Podríamos hablar de una colaboración (mención, contenidos, enlace)?
Gracias, [Tu nombre] — [Correo]`,
      arte: `Asunto: Arte × Cocoon Mind — alimentar la curiosidad cultural

Hola:
Soy [Tu nombre], fundador/a de Cocoon Mind (cocoonmind.com), un compañero de IA dedicado a la curiosidad y al placer de aprender. Su exigencia cultural y su mirada al mundo nos inspiran. ¿Estaría abierto/a a explorar una promoción cruzada y posibles contenidos comunes?
Gracias, [Tu nombre] — [Correo]`,
      wikimedia: `Asunto: Poner en valor el saber libre de Wikimedia con nuestra comunidad

Hola:
Soy [Tu nombre], fundador/a de Cocoon Mind (cocoonmind.com), un compañero de IA que cultiva la curiosidad. Queremos destacar y acreditar el saber libre de Wikimedia ante nuestra comunidad, respetando plenamente sus licencias y sus normas de marca. ¿Podríamos hablar de la mejor manera de hacerlo?
Gracias, [Tu nombre] — [Correo]`,
    },
  },

  nl: {
    langLabel: "Nederlands",
    fullTitle: "Standaard partnerbrief",
    full: `Onderwerp: Voorstel tot samenwerking — Cocoon Mind × [Organisatie]

Beste [Naam / Partnershipteam],

Mijn naam is [Uw naam], oprichter van Cocoon Mind (https://cocoonmind.com), een persoonlijke AI-metgezel die nieuwsgierigheid voedt en een rustige, vreugdevolle band met kennis stimuleert — zonder de druk van cijfers. We ondersteunen lerenden en gezinnen in vier talen (Frans, Engels, Spaans, Nederlands), met sterke aandacht voor ethiek, privacy (AVG) en de bescherming van kinderen.

Ik neem contact op omdat [Organisatie] diep aansluit bij onze missie. [Specifieke zin — bv.: "Uw werk om wetenschap voor iedereen toegankelijk te maken belichaamt precies de geest die wij willen cultiveren."] Het zou een eer zijn om een samenwerking te verkennen.

Wat wij [Organisatie] kunnen bieden:
• Zichtbaarheid bij ons groeiende, betrokken publiek van nieuwsgierige geesten en gezinnen in Europa
• Een uitgelichte plek op onze website en in de app, met een link naar uw bronnen
• Co-creatie van content, gezamenlijke activiteiten of kruispromotie — open voor uw ideeën

Wat wij vriendelijk zouden vragen:
• Uw akkoord om uw logo en naam op onze site te tonen, met een link naar [Organisatie]
• Toegang tot uw officiële merkmiddelen en gebruiksrichtlijnen (brand / press kit)

Alles gebeurt uiteraard volgens uw merkrichtlijnen en wordt vóór publicatie ter goedkeuring voorgelegd.

Zou u openstaan voor een kort gesprek in de komende weken? Ik vertel u graag meer over Cocoon Mind en beantwoord al uw vragen.

Dank voor uw tijd en voor het inspirerende werk dat u doet.

Met vriendelijke groet,
[Uw naam]
Oprichter — Cocoon Mind
[E-mail] · [Telefoon] · https://cocoonmind.com`,
    shortTitle: "Eerste-contact e-mails (kort)",
    short: {
      teded: `Onderwerp: TED-Ed × Cocoon Mind — samen nieuwsgierigheid laten groeien

Hallo,
Ik ben [Uw naam], oprichter van Cocoon Mind (cocoonmind.com), een AI-metgezel die nieuwsgierigheid voedt zonder de druk van cijfers. Uw werk met TED-Ed inspireert ons. Ik verken graag wederzijdse zichtbaarheid (logo + link) en mogelijke gezamenlijke content. Staat u open voor een kort gesprek?
Dank, [Uw naam] — [E-mail]`,
      universcience: `Onderwerp: Universcience × Cocoon Mind — kennis voor iedereen

Hallo,
Ik ben [Uw naam], oprichter van Cocoon Mind (cocoonmind.com), een AI-metgezel die nieuwsgierigheid voedt voorbij cijfers. Uw missie om wetenschap met elk publiek te delen sluit nauw aan bij de onze. Kunnen we praten over een samenwerking (vermelding, content, link)?
Dank, [Uw naam] — [E-mail]`,
      arte: `Onderwerp: Arte × Cocoon Mind — culturele nieuwsgierigheid voeden

Hallo,
Ik ben [Uw naam], oprichter van Cocoon Mind (cocoonmind.com), een AI-metgezel gewijd aan nieuwsgierigheid en leerplezier. Uw culturele lat en open blik op de wereld inspireren ons. Staat u open om kruispromotie en mogelijke gezamenlijke content te verkennen?
Dank, [Uw naam] — [E-mail]`,
      wikimedia: `Onderwerp: De vrije kennis van Wikimedia met onze community waarderen

Hallo,
Ik ben [Uw naam], oprichter van Cocoon Mind (cocoonmind.com), een AI-metgezel die nieuwsgierigheid voedt. We willen de vrije kennis van Wikimedia bij onze community uitlichten en crediteren, met volledig respect voor uw licenties en merkrichtlijnen. Kunnen we bespreken hoe we dit het beste doen?
Dank, [Uw naam] — [E-mail]`,
    },
  },
};
